import { ActionIcon, Button, Group, Modal, Stack, Text, Textarea, TextInput } from "@mantine/core";
import { IconPlus, IconTrash } from "@tabler/icons-react";
import { useState } from "react";
import { ConfirmActionModal } from "@how/ui";
import {
  useDeleteStep,
  usePathwaySteps,
  usePathways,
  useSavePathway,
  useSaveStep,
  type PathwayStep,
} from "./membership-queries";

export interface PathwayModalProps {
  readonly opened: boolean;
  readonly canManage: boolean;
  readonly onClose: () => void;
}

/** The church's pathway and its steps, in order. Most churches have one pathway; this dialog edits the first. */
export function PathwayModal({ opened, canManage, onClose }: PathwayModalProps) {
  const pathways = usePathways();
  const steps = usePathwaySteps();
  const savePathway = useSavePathway();
  const saveStep = useSaveStep();
  const deleteStep = useDeleteStep();
  const pathway = (pathways.data ?? []).find((w) => w.isActive) ?? null;
  const rows: PathwayStep[] = (steps.data ?? []).filter((s) => s.pathwayId === pathway?.id);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [newStep, setNewStep] = useState("");
  const [newStepDescription, setNewStepDescription] = useState("");
  const [removing, setRemoving] = useState<PathwayStep | null>(null);
  return (
    <>
      <Modal opened={opened} onClose={onClose} title="Pathway" radius="lg" size="lg">
        <Stack gap="md">
          {pathway === null ? (
            <Stack gap="sm">
              <Text size="sm" c="dimmed">
                A pathway is your church&apos;s own sequence of steps toward membership. Name it,
                then add its steps.
              </Text>
              {canManage ? (
                <Group align="flex-end">
                  <TextInput
                    label="Pathway"
                    placeholder="Becoming a member"
                    value={name}
                    onChange={(e) => {
                      setName(e.currentTarget.value);
                    }}
                    style={{ flex: 1 }}
                  />
                  <Textarea
                    label="A line about it"
                    autosize
                    minRows={1}
                    value={description}
                    onChange={(e) => {
                      setDescription(e.currentTarget.value);
                    }}
                    style={{ flex: 1 }}
                  />
                  <Button
                    disabled={name.trim() === ""}
                    loading={savePathway.isPending}
                    onClick={() => {
                      savePathway.mutate({ pathwayId: null, name, description, sortOrder: 0 });
                    }}
                  >
                    Create
                  </Button>
                </Group>
              ) : null}
            </Stack>
          ) : (
            <Stack gap="sm">
              <div>
                <Text fw={600}>{pathway.name}</Text>
                {pathway.description === null ? null : (
                  <Text size="sm" c="dimmed">
                    {pathway.description}
                  </Text>
                )}
              </div>
              {rows.length === 0 ? (
                <Text size="sm" c="dimmed">
                  No steps yet.
                </Text>
              ) : (
                rows.map((s, index) => (
                  <Group key={s.id} justify="space-between" wrap="nowrap" align="flex-start">
                    <div>
                      <Text size="sm">
                        {String(index + 1)}. {s.name}
                      </Text>
                      {s.description === null ? null : (
                        <Text size="xs" c="dimmed">
                          {s.description}
                        </Text>
                      )}
                    </div>
                    {canManage ? (
                      <ActionIcon
                        variant="subtle"
                        color="gray"
                        aria-label={`Remove step ${s.name}`}
                        onClick={() => {
                          setRemoving(s);
                        }}
                      >
                        <IconTrash size={16} />
                      </ActionIcon>
                    ) : null}
                  </Group>
                ))
              )}
              {canManage ? (
                <Stack gap="xs">
                  <TextInput
                    label="New step"
                    placeholder="Take the membership class"
                    value={newStep}
                    onChange={(e) => {
                      setNewStep(e.currentTarget.value);
                    }}
                  />
                  <Textarea
                    label="What a member reads about it"
                    autosize
                    minRows={1}
                    value={newStepDescription}
                    onChange={(e) => {
                      setNewStepDescription(e.currentTarget.value);
                    }}
                  />
                  <Group justify="flex-end">
                    <Button
                      leftSection={<IconPlus size={16} />}
                      disabled={newStep.trim() === ""}
                      loading={saveStep.isPending}
                      onClick={() => {
                        saveStep.mutate(
                          {
                            pathwayId: pathway.id,
                            stepId: null,
                            name: newStep,
                            description: newStepDescription,
                            sortOrder: rows.length,
                          },
                          {
                            onSuccess: () => {
                              setNewStep("");
                              setNewStepDescription("");
                            },
                          },
                        );
                      }}
                    >
                      Add step
                    </Button>
                  </Group>
                </Stack>
              ) : null}
            </Stack>
          )}
          {saveStep.isError || savePathway.isError ? (
            <Text size="sm" c="red">
              Not saved. A name must be unique in the pathway.
            </Text>
          ) : null}
          <Group justify="flex-end">
            <Button variant="subtle" onClick={onClose}>
              Close
            </Button>
          </Group>
        </Stack>
      </Modal>
      <ConfirmActionModal
        opened={removing !== null}
        onClose={() => {
          setRemoving(null);
        }}
        title="Remove this step"
        message={
          removing === null
            ? ""
            : `Remove "${removing.name}"? Everyone's completion of it goes with it.`
        }
        confirmLabel="Remove"
        danger
        loading={deleteStep.isPending}
        onConfirm={() => {
          if (removing === null) return;
          deleteStep.mutate(removing.id, {
            onSuccess: () => {
              setRemoving(null);
            },
          });
        }}
      />
    </>
  );
}
